import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 丝袜小说精选合集',
  description: '丝袜小说精选合集致力于为喜爱丝袜小说的读者提供最优质的在线阅读体验，汇聚海量精品原创故事。',
  url: 'https://vipmvpda.cn/about',
  about: {
    '@type': 'Organization',
    name: '丝袜小说精选合集',
    description: '专注于丝袜题材原创小说的在线阅读平台。',
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '丝袜小说精选合集有哪些类型的小说？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们涵盖都市言情、青春校园、职场情感、古风言情、悬疑推理、现代都市等多元题材的丝袜小说，满足不同读者的阅读偏好。每日更新最新章节，确保内容新鲜丰富。',
      },
    },
    {
      '@type': 'Question',
      name: '丝袜小说可以免费阅读吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，本站所有丝袜小说均免费提供在线阅读，无需注册和付费。读者可以随时随地浏览最新章节和热门推荐。',
      },
    },
    {
      '@type': 'Question',
      name: '丝袜小说多久更新一次？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们每日更新最新章节，确保读者每天都能看到新鲜的丝袜小说内容。建议收藏本站，不错过任何精彩故事。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系丝袜小说精选合集的运营团队？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过本页面下方的联系方式与我们取得联系，包括电子邮件、社交媒体等渠道。我们欢迎读者提供宝贵的意见和建议。',
      },
    },
  ],
};

export default function About() {
  return (
    <>
      <SEOHead
        title="关于我们 - 丝袜小说精选合集"
        description="丝袜小说精选合集致力于为喜爱丝袜小说的读者提供最优质的在线阅读体验。了解我们的品牌故事、平台口碑与联系方式，收藏本站不迷路。"
        keywords="丝袜小说,关于我们,丝袜小说平台,丝袜小说阅读,联系方式"
        canonicalUrl="https://vipmvpda.cn/about"
      />
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">关于我们</h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">品牌介绍</h2>
              <p className="text-foreground-700 leading-relaxed mb-3">
                丝袜小说精选合集创立于2024年，是国内领先的丝袜题材原创小说阅读平台。我们汇聚了来自全国各地的优秀作者，致力于为读者提供最高质量的丝袜小说阅读体验。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-3">
                我们的使命是打造一个专注、专业、专精的丝袜小说阅读社区，让每一位喜爱丝袜小说的读者都能在这里找到心仪的作品。从都市言情到青春校园，从职场情感到悬疑推理，我们涵盖多元化的题材类型，满足不同读者的阅读偏好。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                平台自上线以来，累计收录精品原创丝袜小说超过500部，日均更新12篇以上，累计阅读量突破148万次，深受广大读者的喜爱与信赖。
              </p>
            </div>
            <div className="rounded-lg overflow-hidden h-64 md:h-auto">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20literary%20reading%20space%20with%20warm%20lighting%2C%20vintage%20bookshelf%20filled%20with%20novels%2C%20cozy%20reading%20nook%20with%20armchair%2C%20soft%20amber%20lighting%2C%20sophisticated%20library%20atmosphere%2C%20cream%20and%20warm%20brown%20tones&width=800&height=600&seq=about-brand&orientation=landscape"
                alt="丝袜小说精选合集 - 品牌介绍"
                title="丝袜小说精选合集品牌介绍"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Reputation */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
              <h2 className="text-xl font-heading font-semibold text-foreground-900">平台口碑</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { icon: 'ri-star-line', value: '4.9', unit: '分', label: '用户评分', desc: '10万+读者好评' },
                { icon: 'ri-book-read-line', value: '500+', unit: '', label: '精品原创', desc: '持续更新中' },
                { icon: 'ri-user-heart-line', value: '50万+', unit: '', label: '活跃读者', desc: '覆盖全国各省市' },
                { icon: 'ri-thumb-up-line', value: '98%', unit: '', label: '满意度', desc: '读者推荐给朋友' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <i className={`${item.icon} text-2xl text-primary-500`}></i>
                  <div className="mt-2">
                    <span className="text-2xl font-heading font-bold text-foreground-900">{item.value}</span>
                    <span className="text-sm text-foreground-500">{item.unit}</span>
                  </div>
                  <div className="text-sm font-medium text-foreground-800 mt-1">{item.label}</div>
                  <div className="text-xs text-foreground-500 mt-0.5">{item.desc}</div>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              {[
                {
                  name: '林女士',
                  role: '忠实读者',
                  content: '这个平台真的太棒了！每天都有新的丝袜小说更新，内容质量很高，分类也很清晰。我已经推荐给了好几个闺蜜，大家都成了忠实粉丝！',
                  rating: 5,
                },
                {
                  name: '张先生',
                  role: '都市言情爱好者',
                  content: '作为一个丝袜小说爱好者，我几乎看遍了所有平台。丝袜小说精选合集的更新速度和内容质量都是最好的，特别是都市言情分类，每一篇都非常精彩。',
                  rating: 5,
                },
                {
                  name: '王女士',
                  role: '职场白领',
                  content: '工作之余来读一读丝袜小说，是最好的放松方式。这个平台的职场情感分类特别适合我，很多故事都能引起共鸣。界面也很清爽，阅读体验很好。',
                  rating: 4,
                },
              ].map((review, index) => (
                <div key={index} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                        {review.name[0]}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                        <div className="text-xs text-foreground-500">{review.role}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-secondary-500 rounded-full"></div>
              <h2 className="text-xl font-heading font-semibold text-foreground-900">联系我们</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">contact@siwaxiaoshuo.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">微信公众号</h4>
                    <p className="text-sm text-foreground-600">丝袜小说精选合集</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-weibo-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">官方微博</h4>
                    <p className="text-sm text-foreground-600">@丝袜小说精选合集</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">工作时间</h4>
                    <p className="text-sm text-foreground-600">周一至周日 9:00 - 22:00</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                <form
                  className="space-y-4"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d9tc49v7mpqf8k8iifc0"
                  method="POST"
                >
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="about-name"
                      name="name"
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="about-email"
                      name="email"
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200 resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                    style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                  />
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">常见问题</h2>
            </div>

            <div className="space-y-3">
              {[
                {
                  q: '丝袜小说精选合集有哪些类型的小说？',
                  a: '我们涵盖都市言情、青春校园、职场情感、古风言情、悬疑推理、现代都市等多元题材的丝袜小说，满足不同读者的阅读偏好。每日更新最新章节，确保内容新鲜丰富。',
                },
                {
                  q: '丝袜小说可以免费阅读吗？',
                  a: '是的，本站所有丝袜小说均免费提供在线阅读，无需注册和付费。读者可以随时随地浏览最新章节和热门推荐。',
                },
                {
                  q: '丝袜小说多久更新一次？',
                  a: '我们每日更新最新章节，确保读者每天都能看到新鲜的丝袜小说内容。建议收藏本站，不错过任何精彩故事。',
                },
                {
                  q: '如何联系丝袜小说精选合集的运营团队？',
                  a: '您可以通过本页面上方的联系方式与我们取得联系，包括电子邮件、社交媒体等渠道。我们欢迎读者提供宝贵的意见和建议。',
                },
              ].map((faq, index) => (
                <details key={index} className="group bg-background-50 border border-background-200/70 rounded-lg">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h3 className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</h3>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-700 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}